// src/components/jobs/JobCreationForm.jsx
import React, { useState, useEffect } from 'react';
import { 
  Briefcase, DollarSign, Calendar, Clock, MapPin, Tags, 
  FileText, Upload, Plus, X, AlertCircle, CheckCircle,
  Users, Target, Zap
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { jobService } from '../../services/jobService';

const JobCreationForm = ({ onSuccess, editingJob = null }) => {
  const { userProfile } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    skillsRequired: [],
    experienceLevel: 'intermediate',
    budget: '',
    budgetType: 'fixed', // fixed, hourly
    deadline: '',
    urgency: 'medium', // low, medium, high
    location: 'remote',
    expectedDuration: '',
    deliverables: [''],
    requirements: [''],
    preferredWorkingHours: '',
    communicationPreference: 'platform',
    attachments: []
  });

  const categories = [
    'Administrative Support',
    'Data Entry',
    'Customer Service',
    'Content Creation',
    'Social Media Management',
    'Bookkeeping',
    'Research',
    'Email Management',
    'Virtual Reception',
    'Project Management',
    'Marketing Support',
    'Other'
  ];

  const urgencyLevels = [
    { value: 'low', label: 'Standard (7+ days)', color: 'text-green-600 bg-green-50' },
    { value: 'medium', label: 'Medium (3-7 days)', color: 'text-yellow-600 bg-yellow-50' },
    { value: 'high', label: 'Urgent (1-3 days)', color: 'text-red-600 bg-red-50' }
  ];

  const experienceLevels = [
    { value: 'entry', label: 'Entry Level', description: 'Basic skills required' },
    { value: 'intermediate', label: 'Intermediate', description: 'Some experience preferred' },
    { value: 'expert', label: 'Expert', description: 'Advanced skills required' }
  ];

  useEffect(() => {
    if (editingJob) {
      setFormData({
        ...editingJob,
        deadline: editingJob.deadline ? new Date(editingJob.deadline).toISOString().split('T')[0] : '',
        deliverables: editingJob.deliverables || [''],
        requirements: editingJob.requirements || ['']
      });
    }
  }, [editingJob]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (error) setError(null);
  };

  const handleSkillAdd = (skill) => {
    if (skill && !formData.skillsRequired.includes(skill)) {
      setFormData(prev => ({
        ...prev,
        skillsRequired: [...prev.skillsRequired, skill]
      }));
    }
  };

  const handleSkillRemove = (skill) => {
    setFormData(prev => ({
      ...prev,
      skillsRequired: prev.skillsRequired.filter(s => s !== skill)
    }));
  };

  const handleArrayFieldChange = (field, index, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].map((item, i) => i === index ? value : item)
    }));
  };

  const addArrayField = (field) => {
    setFormData(prev => ({
      ...prev,
      [field]: [...prev[field], '']
    }));
  };

  const removeArrayField = (field, index) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  const calculateEstimatedCost = () => {
    if (!formData.budget) return null;
    const budget = parseFloat(formData.budget);
    if (formData.budgetType === 'hourly' && formData.expectedDuration) {
      const hours = parseFloat(formData.expectedDuration);
      return budget * hours;
    }
    return budget;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;

    // Validation
    if (!formData.title.trim() || !formData.description.trim() || !formData.budget) {
      setError('Please fill in all required fields');
      return;
    }

    if (formData.skillsRequired.length === 0) {
      setError('Please add at least one required skill');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const jobData = {
        ...formData,
        clientId: userProfile.uid,
        organizationId: userProfile.organizationId,
        budget: parseFloat(formData.budget),
        deadline: formData.deadline ? new Date(formData.deadline).toISOString() : null,
        deliverables: formData.deliverables.filter(d => d.trim()),
        requirements: formData.requirements.filter(r => r.trim()),
        estimatedCost: calculateEstimatedCost()
      };

      if (editingJob) {
        await jobService.updateJob(editingJob.id, jobData);
      } else {
        await jobService.postJob(userProfile.organizationId, jobData);
      }

      setSuccess(true);
      setTimeout(() => {
        if (onSuccess) onSuccess();
      }, 1500);

    } catch (error) {
      console.error('Error saving job:', error);
      setError(error.message || 'Failed to save job posting');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-green-100 rounded-full flex items-center justify-center">
            <CheckCircle className="text-green-600" size={32} />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            Job {editingJob ? 'Updated' : 'Posted'} Successfully!
          </h3>
          <p className="text-gray-600">
            Your job posting is now live and VAs can start applying.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900 flex items-center">
            <Briefcase className="mr-3 text-blue-600" size={28} />
            {editingJob ? 'Edit Job Posting' : 'Create New Job Posting'}
          </h2>
          <p className="text-gray-600 mt-2">
            Provide detailed information to attract the right virtual assistants
          </p>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-8">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start">
              <AlertCircle className="text-red-500 mr-3 mt-0.5" size={20} />
              <div>
                <h4 className="text-red-800 font-medium">Error</h4>
                <p className="text-red-700 text-sm mt-1">{error}</p>
              </div>
            </div>
          )}

          {/* Basic Information */}
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <FileText className="mr-2" size={20} />
              Basic Information
            </h3>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
              <div className="lg:col-span-8">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Job Title *
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., Data Entry for Customer Database"
                  required
                />
              </div>

              <div className="lg:col-span-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Category *
                </label>
                <select
                  value={formData.category}
                  onChange={(e) => handleInputChange('category', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                >
                  <option value="">Select category</option>
                  {categories.map(category => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Job Description *
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                rows={5}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Describe the job in detail, including what the VA will be doing, any specific requirements, and expected outcomes..."
                required
              />
            </div>
          </div>

          {/* Budget & Timeline */}
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <DollarSign className="mr-2" size={20} />
              Budget & Timeline
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Budget Type *
                </label>
                <select
                  value={formData.budgetType}
                  onChange={(e) => handleInputChange('budgetType', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="fixed">Fixed Price</option>
                  <option value="hourly">Hourly Rate</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {formData.budgetType === 'hourly' ? 'Hourly Rate (ZAR) *' : 'Total Budget (ZAR) *'}
                </label>
                <input
                  type="number"
                  value={formData.budget}
                  onChange={(e) => handleInputChange('budget', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="0"
                  min="0"
                  step="0.01"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Deadline
                </label>
                <input
                  type="date"
                  value={formData.deadline}
                  onChange={(e) => handleInputChange('deadline', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Expected Duration
                </label>
                <input
                  type="text"
                  value={formData.expectedDuration}
                  onChange={(e) => handleInputChange('expectedDuration', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., 10 hours, 2 weeks"
                />
              </div>
            </div>

            {calculateEstimatedCost() && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-blue-800 font-medium">
                  Estimated Total Cost: ZAR {calculateEstimatedCost().toLocaleString()}
                </p>
              </div>
            )}
          </div>

          {/* Requirements */}
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <Target className="mr-2" size={20} />
              Requirements
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Experience Level
                </label>
                <div className="space-y-3">
                  {experienceLevels.map(level => (
                    <label key={level.value} className="flex items-start">
                      <input
                        type="radio"
                        name="experienceLevel"
                        value={level.value}
                        checked={formData.experienceLevel === level.value}
                        onChange={(e) => handleInputChange('experienceLevel', e.target.value)}
                        className="mt-1 text-blue-600 focus:ring-blue-500"
                      />
                      <div className="ml-3">
                        <div className="font-medium text-gray-900">{level.label}</div>
                        <div className="text-sm text-gray-600">{level.description}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Urgency Level
                </label>
                <div className="space-y-3">
                  {urgencyLevels.map(urgency => (
                    <label key={urgency.value} className="flex items-center">
                      <input
                        type="radio"
                        name="urgency"
                        value={urgency.value}
                        checked={formData.urgency === urgency.value}
                        onChange={(e) => handleInputChange('urgency', e.target.value)}
                        className="text-blue-600 focus:ring-blue-500"
                      />
                      <span className={`ml-3 px-3 py-1 rounded-full text-sm font-medium ${urgency.color}`}>
                        {urgency.label}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            {/* Skills Required */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Required Skills *
              </label>
              <div className="flex flex-wrap gap-2 mb-3">
                {formData.skillsRequired.map(skill => (
                  <span
                    key={skill}
                    className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium flex items-center"
                  >
                    {skill}
                    <button
                      type="button"
                      onClick={() => handleSkillRemove(skill)}
                      className="ml-2 text-blue-600 hover:text-blue-800"
                    >
                      <X size={14} />
                    </button>
                  </span>
                ))}
              </div>
              <SkillSelector onSkillAdd={handleSkillAdd} />
            </div>
          </div>

          {/* Deliverables & Requirements */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Expected Deliverables
              </label>
              {formData.deliverables.map((deliverable, index) => (
                <div key={index} className="flex items-center mb-3">
                  <input
                    type="text"
                    value={deliverable}
                    onChange={(e) => handleArrayFieldChange('deliverables', index, e.target.value)}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="e.g., Excel spreadsheet with cleaned data"
                  />
                  {formData.deliverables.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeArrayField('deliverables', index)}
                      className="ml-2 text-red-500 hover:text-red-700"
                    >
                      <X size={16} />
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={() => addArrayField('deliverables')}
                className="text-blue-600 hover:text-blue-700 text-sm font-medium flex items-center"
              >
                <Plus size={16} className="mr-1" />
                Add Deliverable
              </button>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Specific Requirements
              </label>
              {formData.requirements.map((requirement, index) => (
                <div key={index} className="flex items-center mb-3">
                  <input
                    type="text"
                    value={requirement}
                    onChange={(e) => handleArrayFieldChange('requirements', index, e.target.value)}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="e.g., Must have experience with CRM systems"
                  />
                  {formData.requirements.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeArrayField('requirements', index)}
                      className="ml-2 text-red-500 hover:text-red-700"
                    >
                      <X size={16} />
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={() => addArrayField('requirements')}
                className="text-blue-600 hover:text-blue-700 text-sm font-medium flex items-center"
              >
                <Plus size={16} className="mr-1" />
                Add Requirement
              </button>
            </div>
          </div>

          {/* Additional Settings */}
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <Zap className="mr-2" size={20} />
              Additional Settings
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Preferred Working Hours
                </label>
                <input
                  type="text"
                  value={formData.preferredWorkingHours}
                  onChange={(e) => handleInputChange('preferredWorkingHours', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., 9 AM - 5 PM SAST"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Communication Preference
                </label>
                <select
                  value={formData.communicationPreference}
                  onChange={(e) => handleInputChange('communicationPreference', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="platform">Platform Messages</option>
                  <option value="email">Email</option>
                  <option value="video">Video Calls</option>
                  <option value="mixed">Mixed (as needed)</option>
                </select>
              </div>
            </div>
          </div>

          {/* Submit Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 pt-6 border-t border-gray-200">
            <button
              type="submit"
              disabled={loading}
              className="flex-1 sm:flex-none bg-blue-600 text-white px-8 py-3 rounded-lg font-medium hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
              ) : (
                <Briefcase className="mr-2" size={16} />
              )}
              {editingJob ? 'Update Job' : 'Post Job'}
            </button>
            <button
              type="button"
              onClick={() => window.history.back()}
              className="flex-1 sm:flex-none bg-gray-100 text-gray-700 px-8 py-3 rounded-lg font-medium hover:bg-gray-200 focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Skills Selector Component
const SkillSelector = ({ onSkillAdd }) => {
  const [skill, setSkill] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);

  const commonSkills = [
    'Data Entry', 'Email Management', 'Customer Service', 'Social Media',
    'Content Writing', 'Bookkeeping', 'Research', 'Virtual Reception',
    'Project Management', 'Calendar Management', 'CRM Management',
    'Microsoft Office', 'Google Workspace', 'QuickBooks', 'WordPress'
  ];

  const filteredSkills = commonSkills.filter(s => 
    s.toLowerCase().includes(skill.toLowerCase()) && skill.length > 0
  );

  const handleAdd = (skillToAdd) => {
    onSkillAdd(skillToAdd);
    setSkill('');
    setShowSuggestions(false);
  };

  return (
    <div className="relative">
      <div className="flex">
        <input
          type="text"
          value={skill}
          onChange={(e) => {
            setSkill(e.target.value);
            setShowSuggestions(true);
          }}
          onFocus={() => setShowSuggestions(true)}
          className="flex-1 px-4 py-2 border border-gray-300 rounded-l-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          placeholder="Type a skill..."
        />
        <button
          type="button"
          onClick={() => skill.trim() && handleAdd(skill.trim())}
          className="bg-blue-600 text-white px-4 py-2 rounded-r-lg hover:bg-blue-700 flex items-center"
        >
          <Plus size={16} />
        </button>
      </div>

      {showSuggestions && filteredSkills.length > 0 && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-40 overflow-y-auto">
          {filteredSkills.map(suggestionSkill => (
            <button
              key={suggestionSkill}
              type="button"
              onClick={() => handleAdd(suggestionSkill)}
              className="w-full text-left px-4 py-2 hover:bg-gray-50 text-sm"
            >
              {suggestionSkill}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default JobCreationForm;